import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PreFlightItem {
  id: string;
  label: string;
  description: string;
  status: 'passed' | 'failed' | 'pending' | 'checking';
  required: boolean;
  details?: string;
}

export interface ActivationStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  details?: string;
  entities_created?: number;
}

export interface ActivationResult {
  success: boolean;
  entities_created: {
    properties: number;
    units: number;
    accounts: number;
    tenants: number;
    bill_sources: number;
  };
  errors?: string[];
  hypercare_start: string;
  hypercare_end: string;
}

export interface ActivationStatus {
  current_step: number;
  total_steps: number;
  step_name: string;
  progress_percentage: number;
  is_complete: boolean;
  steps: ActivationStep[];
  result?: ActivationResult;
}

// Hook to get real-time pre-flight checklist status
export function usePreFlightChecklist(projectId: string | undefined) {
  return useQuery({
    queryKey: ['preflight-checklist', projectId],
    queryFn: async (): Promise<PreFlightItem[]> => {
      if (!projectId) throw new Error('Project ID required');

      const items: PreFlightItem[] = [];

      // 1. Check all sign-offs are approved
      const { data: signOffs, error: signOffError } = await supabase
        .from('tum_onboarding_stage_signoffs')
        .select('*')
        .eq('project_id', projectId);

      if (signOffError) throw signOffError;

      const approvedSignOffs = signOffs?.filter(s => s.status === 'approved') || [];
      const requiredSignOffs = signOffs?.filter(s => s.required_signoff) || [];
      const allSignOffsApproved = requiredSignOffs.every(s => s.status === 'approved');

      items.push({
        id: 'signoffs',
        label: 'All stage sign-offs approved',
        description: `${approvedSignOffs.length} of ${requiredSignOffs.length} required sign-offs completed`,
        status: allSignOffsApproved ? 'passed' : 'failed',
        required: true,
        details: allSignOffsApproved 
          ? 'All required approvals received' 
          : `Missing: ${requiredSignOffs.filter(s => s.status !== 'approved').map(s => s.stage_name).join(', ')}`,
      });

      // 2. Check data import completed
      const { data: batches, error: batchError } = await supabase
        .from('tum_onboarding_import_batches')
        .select('*')
        .eq('project_id', projectId);

      if (batchError) throw batchError;

      const completedBatches = batches?.filter(b => b.status === 'completed') || [];
      const hasDataImport = completedBatches.length > 0;

      items.push({
        id: 'data_import',
        label: 'Data import and validation complete',
        description: `${completedBatches.length} batch(es) successfully imported`,
        status: hasDataImport ? 'passed' : 'failed',
        required: true,
        details: hasDataImport 
          ? `Imported: ${completedBatches.map(b => b.batch_type).join(', ')}`
          : 'No completed data imports found',
      });

      // 3. Check staged data validation
      const { data: stagedData, error: stagedError } = await (supabase as any)
        .from('tum_onboarding_staged_data')
        .select('validation_status')
        .eq('project_id', projectId);

      if (stagedError) throw stagedError;

      const validRows = stagedData?.filter(s => s.validation_status === 'valid') || [];
      const totalRows = stagedData?.length || 0;
      const validationRate = totalRows > 0 ? (validRows.length / totalRows) * 100 : 0;
      const dataValidated = validationRate >= 95;

      items.push({
        id: 'validation',
        label: 'Data validation threshold met (≥95%)',
        description: `${validRows.length} of ${totalRows} rows validated (${validationRate.toFixed(1)}%)`,
        status: dataValidated ? 'passed' : validationRate >= 80 ? 'pending' : 'failed',
        required: true,
        details: dataValidated 
          ? 'All data meets quality standards'
          : `${totalRows - validRows.length} rows need attention`,
      });

      // 4. Check account configurations
      const { data: configs, error: configError } = await (supabase as any)
        .from('onboarding_account_configurations')
        .select('*')
        .eq('project_id', projectId);

      if (configError) throw configError;

      const configuredAccounts = configs?.filter(c => c.collection_completed) || [];
      const hasConfigs = configuredAccounts.length > 0;

      items.push({
        id: 'configurations',
        label: 'Bill sources and configurations set up',
        description: `${configuredAccounts.length} account(s) configured`,
        status: hasConfigs ? 'passed' : 'pending',
        required: false,
        details: hasConfigs 
          ? 'Bill collection ready to start'
          : 'Configure bill sources for automatic collection',
      });

      // 5. Check customer sign-off specifically
      const customerSignOff = signOffs?.find(s => s.stage_name === 'customer_signoff' || s.signoff_role === 'customer');
      const hasCustomerApproval = customerSignOff?.status === 'approved';

      items.push({
        id: 'customer_signoff',
        label: 'Customer sign-off received',
        description: hasCustomerApproval 
          ? `Approved on ${new Date(customerSignOff.signed_at!).toLocaleDateString()}`
          : 'Awaiting customer approval',
        status: hasCustomerApproval ? 'passed' : 'failed',
        required: true,
        details: hasCustomerApproval 
          ? customerSignOff.comments || 'Customer approved the configuration'
          : 'Customer must approve before go-live',
      });

      return items;
    },
    enabled: !!projectId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Hook to activate project
export function useActivateProject() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      projectId, 
      workspaceId,
      goLiveDate,
      notes 
    }: { 
      projectId: string;
      workspaceId: string;
      goLiveDate: string;
      notes?: string;
    }): Promise<ActivationResult> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('onboarding-activate', {
        body: {
          project_id: projectId,
          workspace_id: workspaceId,
          go_live_date: goLiveDate,
          notes,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data as ActivationResult;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['preflight-checklist', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['activation-status', variables.projectId] });
      
      toast({
        title: 'Activation Successful!',
        description: `Created ${data.entities_created.properties} properties, ${data.entities_created.accounts} accounts. Hypercare period started.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Activation Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook to get activation status/progress
export function useActivationStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ['activation-status', projectId],
    queryFn: async (): Promise<ActivationStatus | null> => {
      if (!projectId) return null;

      // Check if project is already live
      const { data: project, error } = await supabase
        .from('tum_onboarding_projects')
        .select('overall_status, actual_go_live_date')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      if (project?.overall_status === 'live' && project?.actual_go_live_date) {
        return {
          current_step: 5,
          total_steps: 5,
          step_name: 'Activation Complete',
          progress_percentage: 100,
          is_complete: true,
          steps: [
            { id: 'verify', name: 'Verify Pre-Flight', status: 'completed' },
            { id: 'entities', name: 'Create TUM Entities', status: 'completed' },
            { id: 'sources', name: 'Activate Bill Sources', status: 'completed' },
            { id: 'notify', name: 'Send Notifications', status: 'completed' },
            { id: 'hypercare', name: 'Start Hypercare', status: 'completed' },
          ],
          result: {
            success: true,
            entities_created: { properties: 0, units: 0, accounts: 0, tenants: 0, bill_sources: 0 },
            hypercare_start: project.actual_go_live_date,
            hypercare_end: new Date(new Date(project.actual_go_live_date).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          },
        };
      }

      return null;
    },
    enabled: !!projectId,
  });
}

// Hook to check hypercare status
export function useHypercareStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ['hypercare-status', projectId],
    queryFn: async () => {
      if (!projectId) return null;

      const { data: project, error } = await supabase
        .from('tum_onboarding_projects')
        .select('actual_go_live_date, overall_status')
        .eq('id', projectId)
        .single();

      if (error) throw error;
      if (!project?.actual_go_live_date) return null;

      const goLiveDate = new Date(project.actual_go_live_date);
      const hypercareEnd = new Date(goLiveDate.getTime() + 7 * 24 * 60 * 60 * 1000);
      const now = new Date();
      const daysRemaining = Math.ceil((hypercareEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
      const isInHypercare = now < hypercareEnd;

      return {
        start_date: goLiveDate.toISOString(),
        end_date: hypercareEnd.toISOString(),
        days_remaining: Math.max(0, daysRemaining),
        is_active: isInHypercare,
        progress_percentage: isInHypercare 
          ? Math.round(((7 - daysRemaining) / 7) * 100) 
          : 100,
      };
    },
    enabled: !!projectId,
    refetchInterval: 60000, // Refresh every minute
  });
}
